import React from 'react';
import querystring from 'querystring';

import { Main, MainHeader, Body } from 'components/Main';
import { Panel, PanelTop } from 'components/Panel';
import { Table, Th, Td } from 'components/Table';
import PlayerLink from 'components/PlayerLink';
import DropDown from 'components/DropDown';
import GlobalParamsStore from 'stores/GlobalParamsStore';
import { dp } from 'components/util';
import { FixturesTable } from 'views/fixtures/FixturesPage';

export default class TeamPage extends React.Component {
    componentDidMount() {
        if (window !== undefined) {
            window.addEventListener('popstate', event => {
                let params = {};
                if (window.location.search) {
                    params = querystring.parse(window.location.search.substring(1));
                }
                this.props.browserBack(window.location.pathname, params);
            });
        }
    }

    render() {
        const {
            batting = [],
            bowling = [],
            fielding = [],
            fixtures = [],
            leagueTable,
            record = {},
            team = {},
            season,
            pendingSeason,
            filterSeason
        } = this.props;
        return (
            <Main>
                <MainHeader />
                <Body id="team-page">
                    <div className="cc-team-page-title">
                        <h1>{team.name}</h1>
                        <SeasonDropDown season={season} pendingSeason={pendingSeason} filterSeason={filterSeason} />
                    </div>
                    <TeamRecord record={record} />
                    {leagueTable ? <LeagueTableLink table={leagueTable} /> : null}
                    <div className="cc-team-stat-panels">
                        <Panel border className="cc-team-stat-panel">
                            <PanelTop title="Top Batsmen">
                                <StatsSeeAllLink href="/batting-averages" team={team} season={season} />
                            </PanelTop>
                            <BattingLeadersTable batting={batting} />
                        </Panel>
                        <Panel border className="cc-team-stat-panel">
                            <PanelTop title="Top Bowlers">
                                <StatsSeeAllLink href="/bowling-averages" team={team} season={season} />
                            </PanelTop>
                            <BowlingLeadersTable bowling={bowling} />
                        </Panel>
                        <Panel border className="cc-team-stat-panel">
                            <PanelTop title="Top Fielders">
                                <StatsSeeAllLink href="/fielding-records" team={team} season={season} />
                            </PanelTop>
                            <FieldingLeadersTable fielding={fielding} />
                        </Panel>
                    </div>
                    <div id="fixtures-page">
                        <Panel border>
                            <PanelTop title="Fixtures & Results" />
                            <FixturesTable fixtures={fixtures} />
                        </Panel>
                    </div>
                </Body>
            </Main>
        );
    }
}

const StatsSeeAllLink = ({ href, team, season }) => {
    const teamMenu = GlobalParamsStore.getMenuConfig().filter(item => item.id === 'team')[0];
    const teamItem = teamMenu.items.filter(item => item.text === team.name)[0];
    const params = { team: teamItem ? teamItem.value : team.tid, season, type: 'HL' };
    return <a className="cc-team-see-all" href={`${href}?${querystring.stringify(params)}`}>see all</a>;
};

const SeasonDropDown = ({ season, pendingSeason, filterSeason }) => {
    const seasonMenu = Object.assign(
        {},
        GlobalParamsStore.getMenuConfig().filter(item => item.id === 'season')[0],
        {
            onChange: filterSeason
        }
    );
    return (
        <div className="cc-global-params-buttons">
            <ul>
                <li>
                    <DropDown data={seasonMenu} value={pendingSeason || season} loading={pendingSeason !== undefined} />
                </li>
            </ul>
        </div>
    );
};

const LeagueTableLink = ({ table }) => (
    <div className="cc-team-league-table">
        <a href={table.link} target="_blank">
            {table.description}
        </a>
    </div>
);

const TeamRecord = ({ record }) => (
    <dl className="cc-team-record">
        <div>
            <dt>Played</dt>
            <dd>{record.played || 0}</dd>
        </div>
        <div>
            <dt>Won</dt>
            <dd>{record.won || 0}</dd>
        </div>
        <div>
            <dt>Drawn</dt>
            <dd>{record.drawn || 0}</dd>
        </div>
        <div>
            <dt>Lost</dt>
            <dd>{record.lost || 0}</dd>
        </div>
        <div>
            <dt>No Result</dt>
            <dd>{record.noresult || 0}</dd>
        </div>
    </dl>
);

const BattingLeadersTable = ({ batting }) => (
    <Table striped hover compact>
        <thead>
            <tr>
                <Th left>Name</Th>
                <Th min="2">Runs</Th>
                <Th priority="2">Best</Th>
                <Th priority="2" short="3" value="100">
                    100s
                </Th>
                <Th priority="2" short="2" value="50">
                    50s
                </Th>
                <Th priority="2">4s</Th>
                <Th priority="2">6s</Th>
                <Th short="3" value="Inns">
                    Innings
                </Th>
                <Th priority="2" short="3" value="No">
                    Not Outs
                </Th>
                <Th min="2" short="2" value="Avg">
                    Average
                </Th>
                <Th priority="2">S/R</Th>
            </tr>
        </thead>
        <tbody>{batting.map((row, index) => <BattingLeadersRow key={row.pid} index={index} row={row} />)}</tbody>
    </Table>
);

const BattingLeadersRow = ({ row, index }) => (
    <tr className={index === 0 ? 'cc-team-leading-player' : ''}>
        <Td left>
            <PlayerLink pid={row.pid} fname={row.fname} lname={row.lname} />
        </Td>
        <Td min="2">{row.runs}</Td>
        <Td priority="2">{row.bestruns}</Td>
        <Td priority="2">{row.r100s}</Td>
        <Td priority="2">{row.r50s}</Td>
        <Td priority="2">{row.fours}</Td>
        <Td priority="2">{row.sixes}</Td>
        <Td>{row.innings}</Td>
        <Td priority="2">{row.notouts}</Td>
        <Td min="2">{dp(row.average)}</Td>
        <Td priority="2">{dp(row.strike_rate, 1)}</Td>
    </tr>
);

const BowlingLeadersTable = ({ bowling }) => (
    <Table striped hover compact>
        <thead>
            <tr>
                <Th left>Name</Th>
                <Th min="2" short="3" value="W">
                    Wickets
                </Th>
                <Th min="2" short="3" value="O">
                    Overs
                </Th>
                <Th priority="2" short="3" value="R">
                    Runs
                </Th>
                <Th min="2" short="3" value="Avg">
                    Average
                </Th>
                <Th priority="2" short="4" value="E/R">
                    Economy Rate
                </Th>
            </tr>
        </thead>
        <tbody>{bowling.map((row, index) => <BowlingLeadersRow key={row.pid} index={index} row={row} />)}</tbody>
    </Table>
);

const BowlingLeadersRow = ({ row, index }) => (
    <tr className={index === 0 ? 'cc-team-leading-player' : ''}>
        <Td left>
            <PlayerLink pid={row.pid} fname={row.fname} lname={row.lname} />
        </Td>
        <Td min="2">{row.wickets}</Td>
        <Td min="2">{row.overs}</Td>
        <Td priority="2">{row.runs}</Td>
        <Td min="2">{dp(row.average)}</Td>
        <Td priority="2">{dp(row.economy)}</Td>
    </tr>
);

const FieldingLeadersTable = ({ fielding }) => (
    <Table striped hover compact>
        <thead>
            <tr>
                <Th left>Name</Th>
                <Th short="3" value="Vics">
                    Victims
                </Th>
                <Th short="3" value="Ct">
                    Caught
                </Th>
                <Th short="3" value="Ro">
                    Run Out
                </Th>
                <Th short="3" value="St">
                    Stumped
                </Th>
            </tr>
        </thead>
        <tbody>{fielding.map((row, index) => <FieldingLeadersRow key={row.pid} index={index} row={row} />)}</tbody>
    </Table>
);

const FieldingLeadersRow = ({ row, index }) => (
    <tr className={index === 0 ? 'cc-team-leading-player' : ''}>
        <Td left>
            <PlayerLink pid={row.pid} fname={row.fname} lname={row.lname} />
        </Td>
        <Td>{row.victims}</Td>
        <Td>{row.caught}</Td>
        <Td>{row.runout}</Td>
        <Td>{row.stumped}</Td>
    </tr>
);



// WEBPACK FOOTER //
// ./src/views/team/TeamPage.jsx